import PrizeAmount from "@components/utils/PrizeAmount";
import { TextGradient } from "@components/utils/TextGradient";
import { motion } from "framer-motion";
import { cn, PropsWithClassName } from "kz-ui-sdk";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import StarBurst from "../../../StarBurst";

export interface DualClaimableCatsRef {
  startClaimAnimation: () => void;
}

interface DualClaimableCatsProps extends PropsWithClassName {
  className?: string;
  title: string;
  subTitle?: string;
  prizeAmount: number;
}

const DualClaimableCats = forwardRef<DualClaimableCatsRef, DualClaimableCatsProps>(
  ({ className, title, subTitle, prizeAmount }, ref) => {
    const [isClaiming, setIsClaiming] = useState(false);

    const startClaimAnimation = useCallback(() => {
      setIsClaiming(true);
    }, []);

    useImperativeHandle(ref, () => ({
      startClaimAnimation,
    }));

    return (
      <div className={cn("relative h-[414px] w-[414px] min-w-[414px] overflow-x-hidden", className)}>
        {/* STAR BURST */}
        <div className="absolute left-1/2 top-0 h-[180px] w-[180px] -translate-x-1/2">
          <StarBurst
            size={{ width: 180, height: 180 }}
            numberOfStars={16}
            duration={2}
            className="left-0 top-[42px]"
            animate
            starSize={{ width: 28, height: 28 }}
          />
        </div>

        {/* CAT LEFT */}
        <div className="pointer-events-none absolute left-[-26px] top-0 h-[335px] w-[159px] overflow-visible">
          <motion.img
            src="/images/mega-share/claimable-cat-left.png"
            alt="cat left"
            className="h-[335px] w-[159px] object-contain will-change-transform"
            initial={{
              rotate: 0,
              x: 0,
            }}
            animate={
              isClaiming
                ? {
                    rotate: [0, 0, -1.2, -1.2, 0],
                    x: [0, 0, -10],
                  }
                : {
                    rotate: [0, 0, -1.2, -1.2, 0],
                    x: 0,
                  }
            }
            transition={
              isClaiming
                ? {
                    duration: 2,
                    times: [0, 0.5, 1],
                    ease: "easeOut",
                  }
                : {
                    duration: 4,
                    repeat: Infinity,
                    times: [0, 0.5, 0.625, 0.875, 1],
                  }
            }
          />
        </div>

        {/* CAT RIGHT */}
        <div className="pointer-events-none absolute right-[-26px] top-0 h-[335px] w-[159px] overflow-visible">
          <motion.img
            src="/images/mega-share/claimable-cat-right.png"
            alt="cat right"
            className="h-[335px] w-[159px] object-contain will-change-transform"
            initial={{
              rotate: 0,
              x: 0,
            }}
            animate={
              isClaiming
                ? {
                    rotate: [0, 0, 1.2, 1.2, 0],
                    x: [0, 0, 10],
                  }
                : {
                    rotate: [0, 0, 1.2, 1.2, 0],
                    x: 0,
                  }
            }
            transition={
              isClaiming
                ? {
                    duration: 2,
                    times: [0, 0.5, 1],
                    ease: "easeOut",
                  }
                : {
                    duration: 4,
                    repeat: Infinity,
                    times: [0, 0.5, 0.625, 0.875, 1],
                  }
            }
          />
        </div>

        {/* GLOW */}
        <motion.img
          src="/images/mega-share/box-glow-unlocked.png"
          alt="glow"
          className="will-change-opacity absolute left-1/2 top-[80px] h-[120px] w-[320px] -translate-x-1/2"
          initial={{
            opacity: 0,
          }}
          animate={
            isClaiming
              ? {
                  opacity: [1, 1, 0],
                }
              : {
                  opacity: [0, 0, 1, 1, 0],
                }
          }
          transition={
            isClaiming
              ? {
                  duration: 1.5,
                  times: [0, 0.667, 1],
                  ease: "easeOut",
                }
              : {
                  duration: 4,
                  repeat: Infinity,
                  times: [0, 0.6, 0.7, 0.875, 1],
                }
          }
        />

        {/* BOX TOP */}
        <img
          src="/images/mega-share/claimable-box-top.png"
          alt="box top"
          className="absolute left-0 top-0 h-[168px] w-[414px] object-contain"
        />

        {/* BOX GLOW BEHIND */}
        <motion.img
          src="/images/mega-share/box-glow-behind.png"
          alt="box glow behind"
          className="absolute left-1/2 top-[50px] h-[140px] w-[230px]"
          initial={{
            opacity: 1,
            x: "-50%",
            y: 0,
          }}
          animate={
            isClaiming
              ? {
                  opacity: [1, 1, 0],
                  x: "-50%",
                  y: [0, -10, -10],
                }
              : {
                  opacity: 1,
                  x: "-50%",
                  y: 0,
                }
          }
          transition={
            isClaiming
              ? {
                  duration: 1.5,
                  times: [0, 0.667, 1],
                  ease: "easeOut",
                }
              : {}
          }
        />

        {/* COINS */}
        <div className="absolute left-1/2 top-[92px] h-[100px] w-full translate-x-[-50%]">
          {/* COIN RIGHT */}
          <motion.img
            src="/images/icon-coin-2.png"
            alt="coin 3"
            className="absolute left-[calc(50%+12px)] top-[18px] h-[112px] w-[112px] object-contain"
            initial={{
              scale: 1,
              rotate: 10,
              x: 0,
              y: 0,
              opacity: 1,
            }}
            animate={
              isClaiming
                ? {
                    x: [0, 0, 70],
                    y: [0, 0, -200],
                    scale: [1, 1.05, 0.1],
                    opacity: [1, 1, 0.8],
                  }
                : {
                    scale: [1, 1, 1.08, 1.08, 1],
                  }
            }
            transition={
              isClaiming
                ? {
                    duration: 1.5,
                    times: [0, 0.667, 1],
                    ease: "easeOut",
                  }
                : {
                    duration: 4,
                    repeat: Infinity,
                    times: [0, 0.5, 0.625, 0.875, 1],
                  }
            }
          />
          {/* COIN CENTER */}
          <motion.img
            src="/images/icon-coin-2.png"
            alt="coin 2"
            className="absolute left-[calc(50%-59px)] top-[0px] h-[124px] w-[124px] object-contain"
            initial={{
              scale: 1,
              rotate: -5,
              x: 0,
              y: 0,
              opacity: 1,
            }}
            animate={
              isClaiming
                ? {
                    x: [0, 0, 130],
                    y: [0, 0, -200],
                    scale: [1, 1.05, 0.1],
                    opacity: [1, 1, 0.8],
                  }
                : {
                    scale: [1, 1, 1.08, 1.08, 1],
                  }
            }
            transition={
              isClaiming
                ? {
                    duration: 1.5,
                    times: [0, 0.667, 1],
                    ease: "easeOut",
                  }
                : {
                    duration: 4,
                    repeat: Infinity,
                    times: [0, 0.5, 0.625, 0.875, 1],
                  }
            }
          />

          {/* COIN LEFT */}
          <motion.img
            src="/images/icon-coin-2.png"
            alt="coin 1"
            className="absolute left-[calc(50%-122px)] top-[20px] h-[105px] w-[105px] object-contain"
            initial={{
              scale: 1,
              rotate: -10,
              x: 0,
              y: 0,
              opacity: 1,
            }}
            animate={
              isClaiming
                ? {
                    x: [0, 0, 180],
                    y: [0, 0, -200],
                    scale: [1, 1.05, 0.1],
                    opacity: [1, 1, 0.8],
                  }
                : {
                    scale: [1, 1, 1.08, 1.08, 1],
                  }
            }
            transition={
              isClaiming
                ? {
                    duration: 1.5,
                    times: [0, 0.667, 1],
                    ease: "easeOut",
                  }
                : {
                    duration: 4,
                    repeat: Infinity,
                    times: [0, 0.5, 0.625, 0.875, 1],
                  }
            }
          />
        </div>

        {/* BOX GLOW FRONT */}
        <motion.img
          src="/images/mega-share/box-glow.png"
          alt="box glow front"
          className="absolute left-1/2 top-[96px] h-[120px] w-[230px]"
          initial={{
            opacity: 1,
            x: "-50%",
            y: 0,
          }}
          animate={
            isClaiming
              ? {
                  opacity: [1, 1, 0],
                  x: "-50%",
                  y: [0, -20, -20],
                }
              : {
                  opacity: 1,
                  x: "-50%",
                  y: 0,
                }
          }
          transition={
            isClaiming
              ? {
                  duration: 1.5,
                  times: [0, 0.667, 1],
                  ease: "easeOut",
                }
              : {}
          }
        />

        {/* BOX BOTTOM */}
        <img
          src="/images/mega-share/claimable-box-bottom.png"
          alt="box bottom"
          className="absolute bottom-0 left-0 h-[246px] w-[414px] object-contain"
        />

        {/* CAT LEFT HAND */}
        <div className="pointer-events-none absolute left-[41px] top-[175px] h-[44px] w-[54px] overflow-visible">
          <motion.img
            src="/images/mega-share/claimable-cat-left-hand.png"
            alt="cat left hand"
            className="h-[44px] w-[54px] origin-center"
            style={{ willChange: "transform" }}
            initial={{
              rotate: 0,
              x: 0,
            }}
            animate={
              isClaiming
                ? {
                    rotate: [0, 0, -15, -15, 0],
                    x: [0, 0, -10],
                  }
                : {
                    rotate: [0, 0, -15, -15, 0],
                    x: 0,
                  }
            }
            transition={
              isClaiming
                ? {
                    duration: 2,
                    times: [0, 0.5, 1],
                    ease: "easeOut",
                  }
                : {
                    duration: 4,
                    repeat: Infinity,
                    times: [0, 0.5, 0.625, 0.875, 1],
                  }
            }
          />
        </div>

        {/* CAT RIGHT HAND */}
        <div className="pointer-events-none absolute right-[39px] top-[175px] h-[44px] w-[54px] overflow-visible">
          <motion.img
            src="/images/mega-share/claimable-cat-right-hand.png"
            alt="cat right hand"
            className="h-[44px] w-[54px] origin-center"
            style={{ willChange: "transform" }}
            initial={{
              rotate: 0,
              x: 0,
            }}
            animate={
              isClaiming
                ? {
                    rotate: [0, 0, 15, 15, 0],
                    x: [0, 0, 10],
                  }
                : {
                    rotate: [0, 0, 15, 15, 0],
                    x: 0,
                  }
            }
            transition={
              isClaiming
                ? {
                    duration: 2,
                    times: [0, 0.5, 1],
                    ease: "easeOut",
                  }
                : {
                    duration: 4,
                    repeat: Infinity,
                    times: [0, 0.5, 0.625, 0.875, 1],
                  }
            }
          />
        </div>

        {/* TITLE */}
        <div className="absolute left-1/2 top-[190px] h-[100px] w-full -translate-x-1/2">
          {subTitle && <p className="text-center text-sm leading-5 text-content-soft">{subTitle}</p>}
          <TextGradient
            text={title}
            className="whitespace-nowrap"
          />
        </div>

        {/* PRIZE AMOUNT */}
        <div className="absolute left-1/2 top-[236px] h-[100px] w-full -translate-x-1/2 text-center">
          <PrizeAmount
            fontSize={"56px"}
            className="h-[67px] font-black"
            amount={prizeAmount}
          />
        </div>
      </div>
    );
  },
);

DualClaimableCats.displayName = "DualClaimableCats";

export default DualClaimableCats;
